import React, {PropsWithChildren, useEffect, useState} from "react";
import {getShopId} from "../lib/getShopId";
import {INewsletter} from "../../typescript-library";
import Checkbox from "../lib/form/Checkbox/Checkbox";
import Select from "../molecules/Select";
import {useTranslation} from "react-i18next";
import Spinner from "../utils/loader/Spinner";
import {useForm, Controller} from "react-hook-form";
import styles from "./NewsLetterOptionsForm.module.scss";
import {RegistrationVoucher} from "../../helpers/registrationVoucher";
import {useCustomerSelector} from "../../store";

type CustomerData = {gender: "F" | "M" | null; firstname: string; lastname: string; birthday: string};
const customSortArray = [100014, 100000, 100008, 100007, 100009, 100015, 100018];
export const deletedNewsletterTypes = [100010, 100012, 100013, 100016, 100017, 100020, 100022];
const removeCheckedArray = [100009];
const disabledCheckedArray = [100014];

const NewsLetterOptionsForm: React.FC = () => {
  const {customer} = useCustomerSelector();
  const {t} = useTranslation();
  const [nlOptionsState, setNlOptionsState] = useState<INewsletter[]>([]);
  const [customerData, setCustomerData] = useState<CustomerData>();
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [birthday, setBirthday] = useState<string | undefined>();
  const [successState, setSuccessState] = useState<{
    updateSubscribe?: boolean;
    firstSubscribe?: boolean;
  }>({firstSubscribe: true});

  useEffect(() => {
    void customer.getNewsletterOptions(getShopId()).then((data) => {
      const filteredOption = data.filter((option) => !deletedNewsletterTypes.includes(option.newsletterTypeId));
      const filteredOptionCopy = [...filteredOption];

      filteredOptionCopy.forEach((option, index) => {
        filteredOption[index] = {
          ...filteredOption[index],
          isSubscribed: !removeCheckedArray.includes(option.newsletterTypeId),
        };
      });
      setNlOptionsState(filteredOption);
    });
  }, []);

  useEffect(() => {
    setButtonDisabled(nlOptionsState.every((option) => !option.isSubscribed));
  }, [nlOptionsState]);

  const submitData = async () => {
    if (nlOptionsState.find((option) => option.newsletterTypeId === 100009)?.isSubscribed) {
      void (await customer.putProperties(getShopId(), {birthday}));

      if (!birthday) {
        setBirthday(undefined);

        return;
      }
    }

    setIsLoading(true);
    await customer.putProperties(getShopId(), {
      firstName: customerData?.firstname,
      lastName: customerData?.lastname,
      birthday,
      gender: customerData?.gender || null,
    });
    await customer.setNewsletterOptions(getShopId(), nlOptionsState);
    setIsLoading(false);
    setSuccessState({updateSubscribe: true});
    setButtonDisabled(true);
    window.scrollTo({top: 0, left: 0, behavior: "smooth"});
  };

  const isGermanShop = [100001, 100009, 100017].includes(getShopId());

  const MessageBlock = () => (
    <SuccessMessageBlock>
      {t("newsletter.subscriptionSuccessful")}
      {RegistrationVoucher.enabled && (
        <>
          <br />
          {t("newsletter.welcomeDiscountSeparateEmail--amountFormatted", {
            amountFormatted: RegistrationVoucher.amountFormatted,
          })}
        </>
      )}
    </SuccessMessageBlock>
  );

  return (
    <div>
      {successState.firstSubscribe && <MessageBlock />}
      {successState.updateSubscribe && (
        <SuccessMessageBlock>{t("newsletter.changesSavedSuccessfully")}</SuccessMessageBlock>
      )}

      {isGermanShop && <BrandOfTheMonthButton />}
      <div className={styles.formsWrapper}>
        <div>
          <PersonalDataForm
            onChange={(data) => {
              setButtonDisabled(false);
              setCustomerData(data);
            }}
          />
        </div>
        <OptionsForm
          nlOptions={nlOptionsState}
          onChange={(options) => {
            setNlOptionsState(options);
            setButtonDisabled(false);
          }}
          isBirthdayRequired={birthday === undefined}
          showBirthdayInput={nlOptionsState.some((option) => option.newsletterTypeId === 100009 && option.isSubscribed)}
          onChangeBirthday={(data) => {
            setBirthday(data);
          }}
        />
      </div>
      <div className={styles.buttonWrapper}>
        <button disabled={buttonDisabled} className={styles.submitButton} onClick={() => void submitData()}>
          {isLoading ? <Spinner /> : <span>{t("newsletter.saveSettings")}</span>}
        </button>
      </div>
    </div>
  );
};

const BrandOfTheMonthButton = () => {
  return (
    <div className={styles.brandOfTheMonthButtonWrapper}>
      <div className={styles.title}>{"MARKE DES MONATS \u2013 Das Highlight für alle Newsletterabonnent:innen"}</div>
      <a target={"_blank"} rel={"noreferrer"} href={"https://www.connox.de/marke-des-monats.html"}>
        {"Jetzt alle Aktionen ansehen"}
      </a>
    </div>
  );
};

const PersonalDataForm: React.FC<{onChange: (data: CustomerData) => void}> = ({onChange}) => {
  const {t} = useTranslation();
  const {register, watch, control} = useForm<CustomerData>({});

  return (
    <div className={styles.personalDataFormWrapper}>
      <div className={styles.title}>{t("form.howToAddress")}</div>
      <form
        onChange={() => {
          onChange(watch());
        }}
      >
        <div className={styles.inputWrapper}>
          <div className={styles.labelText}>{t("form.title")}</div>
          <Controller
            control={control}
            render={({field}) => (
              <Select
                id="SelectTitle"
                styles={{wrapper: styles.select}}
                onChange={(value) => {
                  field.onChange(value);
                  onChange(watch());
                }}
                placeHolder={t("form.pleaseChoose")}
                label={t("form.title")}
                showLabel={false}
                options={[
                  {value: "F", text: t("form.ms")},
                  {value: "M", text: t("form.mr")},
                  {value: undefined, text: t("general.noSpecification")},
                ]}
              />
            )}
            name={"gender"}
          />
        </div>

        <div className={styles.inputWrapper}>
          <div className={styles.labelText}>{t("form.firstname")}</div>
          <input className={styles.input} {...register("firstname")} />
        </div>

        <div className={styles.inputWrapper}>
          <div className={styles.labelText}>{t("form.lastname")}</div>
          <input className={styles.input} {...register("lastname")} />
        </div>
      </form>
    </div>
  );
};

export const SuccessMessageBlock: React.FC<PropsWithChildren> = ({children}: {children}) => {
  return (
    <div className={styles.successMessageWrapper}>
      <i className={"icon icon-check--green icon-size--28"} />
      <div>{children}</div>
    </div>
  );
};

export const OptionsForm: React.FC<{
  nlOptions: INewsletter[];
  onChange: (options: INewsletter[]) => void;
  showBirthdayInput?: boolean;
  onChangeBirthday?: (birthday: string) => void;
  isBirthdayRequired?: boolean;
}> = ({nlOptions, onChange, showBirthdayInput = false, onChangeBirthday, isBirthdayRequired = false}) => {
  const [isAllChecked, setIsAllChecked] = useState(false);
  const {t} = useTranslation();
  const {customer} = useCustomerSelector();
  const [birthday, setBirthday] = useState<string | null>();

  useEffect(() => {
    void customer.retrieveProperties(getShopId()).then((data) => {
      setBirthday(data?.birthday);
      if (onChangeBirthday) {
        onChangeBirthday(data?.birthday ?? "");
      }
    });
  }, []);

  useEffect(() => {
    setIsAllChecked(nlOptions.every((option) => option.isSubscribed));
  }, [nlOptions]);

  return (
    <div className={styles.optionsFormWrapper}>
      <div className={styles.title}>{t("newsletter.newsletterTopics")}</div>
      <div className={styles.checkboxWrapper}>
        <Checkbox
          className={styles.checkbox}
          checked={isAllChecked}
          color={"green"}
          onChange={(checked) => {
            setIsAllChecked(checked);
            const options = [...nlOptions];
            options.forEach((option, index) => {
              if (!disabledCheckedArray.includes(option.newsletterTypeId)) {
                options[index] = {...option, isSubscribed: checked};
              }
            });
            onChange(options);
          }}
        >
          <p className={styles.checkboxTitle}>{t("newsletter.allTopics")}</p>
        </Checkbox>
      </div>
      {customSortArray.map((id) => {
        const option = nlOptions.find((optionToFind) => optionToFind.newsletterTypeId === id);
        const index = nlOptions.findIndex((optionToFind) => optionToFind.newsletterTypeId === id);
        if (!option) {
          return null;
        }

        return (
          <div key={option.newsletterTypeId} className={styles.checkboxWrapper}>
            <Checkbox
              className={styles.checkbox}
              checked={option.isSubscribed}
              onChange={(checked) => {
                const options = [...nlOptions];
                options[index] = {...options[index], isSubscribed: checked};
                onChange(options);
              }}
              color={disabledCheckedArray.includes(option.newsletterTypeId) ? "lightgrey" : "green"}
              disabled={disabledCheckedArray.includes(option.newsletterTypeId)}
            >
              <div className={styles.checkboxContent}>
                <p className={styles.checkboxTitle}>{option.newsletterTypeName}</p>
                <p className={styles.checkboxSubtitle}>{option.newsletterTypeDescription}</p>
                {option.newsletterTypeId === 100009 && showBirthdayInput && (
                  <div className={styles.inputWrapper}>
                    <div className={styles.labelText}>{t("form.birthday")}</div>
                    <input
                      type={"date"}
                      className={styles.input}
                      value={birthday || ""}
                      onChange={(e) => {
                        setBirthday(e.target.value);
                        if (onChangeBirthday) {
                          onChangeBirthday(e.target.value);
                        }
                      }}
                    />
                    {!birthday && isBirthdayRequired && (
                      <span className={styles.error}>{t("newsletter.enterBirthday")}</span>
                    )}
                  </div>
                )}
              </div>
            </Checkbox>
          </div>
        );
      })}
    </div>
  );
};

export default NewsLetterOptionsForm;
